import { useCallback, useMemo, useState } from 'react';
import { addHours } from 'date-fns';

import { generateSshAccessKeyApi } from '../../../utilities/api/getDataFromApi';
import useMfa from '../../MfaModal/hooks/useMfa';
import { MAX_SCHEDULE_HOURS, START_MODE } from '../constants';
import { generateFailureBanner, generateSuccessBanner } from '../helpers';

/**
 * Triggers the browser download of the generated key zip.
 * @param {Blob} blob - the zip payload returned by the API
 */
const downloadKeyZip = (blob) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');

  a.href = url;
  const dateStr = new Date().toISOString().split('T')[0];

  a.download = `saviynt-ssh-keys-${dateStr}.zip`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

/**
 * Owns all DownloadKeysModal form state: passphrase entry, start-time scheduling,
 * derived validation flags, and submission. Keeps the modal components presentational.
 *
 * @param {Object} params
 * @param {number} params.keyValidityHours - hours a generated key stays valid
 * @param {boolean} params.isPassphraseMandatory - whether a passphrase is required
 * @param {number} params.minPassphraseLength - minimum passphrase length
 * @param {number} params.maxPassphraseLength - maximum passphrase length
 * @param {number} params.nextKeySlot - slot the new key will occupy
 * @param {Function} params.onSubmit - called after a successful download
 * @param {Function} params.onClose - closes the modal
 * @param {Function} params.setAlertBannerData - appends alert banners
 * @param {Function} params.localize - the localize function from useLocalize
 * @returns {Object} form state, derived flags, and handlers
 */
const useDownloadKeyForm = ({
  keyValidityHours,
  isPassphraseMandatory,
  minPassphraseLength,
  maxPassphraseLength,
  nextKeySlot,
  onSubmit,
  onClose,
  setAlertBannerData,
  localize,
}) => {
  // `now` is used only as the initial reference for maxScheduleDate and the
  // "Start now" activeFrom display. Anything that needs to compare against the
  // actual current time must call `new Date()` at evaluation time — see
  // filterTime, handleTimeChange, and scheduleValid.
  const now = new Date();
  const runWithMfa = useMfa();

  const [passphrase, setPassphrase] = useState('');
  const [confirmPassphrase, setConfirmPassphrase] = useState('');
  const [showPassphrase, setShowPassphrase] = useState(false);
  const [startMode, setStartMode] = useState(START_MODE.NOW);
  const [scheduledDate, setScheduledDate] = useState(null);
  const [scheduledTime, setScheduledTime] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const maxScheduleDate = useMemo(() => addHours(now, MAX_SCHEDULE_HOURS), []);

  const activeFrom = useMemo(
    () => (startMode === START_MODE.NOW ? now : scheduledTime || now),
    [startMode, scheduledTime]
  );
  const expiresAt = useMemo(
    () => addHours(activeFrom, keyValidityHours),
    [activeFrom, keyValidityHours]
  );

  // --- Derived validation ---

  const hasPassphrase = passphrase.length > 0;

  const passphrasesMismatch =
    hasPassphrase &&
    confirmPassphrase.length > 0 &&
    passphrase !== confirmPassphrase;
  const passphraseLengthInvalid =
    (hasPassphrase && passphrase.length < minPassphraseLength) ||
    passphrase.length > maxPassphraseLength;

  const passphraseValid =
    (!isPassphraseMandatory && !hasPassphrase) ||
    (hasPassphrase &&
      passphrase === confirmPassphrase &&
      !passphraseLengthInvalid);

  const isScheduleMode = startMode === START_MODE.SCHEDULE;
  const scheduleValid =
    !isScheduleMode ||
    (scheduledDate !== null &&
      scheduledTime !== null &&
      scheduledTime.getTime() > Date.now() &&
      scheduledTime.getTime() <= maxScheduleDate.getTime());

  const canSubmit = passphraseValid && scheduleValid;

  const isPassphraseCritical =
    isSubmitted &&
    (passphraseLengthInvalid || (isPassphraseMandatory && !hasPassphrase));
  const isConfirmCritical =
    isSubmitted && (passphrasesMismatch || confirmPassphrase.length === 0);
  const isDateCritical = isSubmitted && isScheduleMode && !scheduledDate;
  const isTimeCritical = isSubmitted && isScheduleMode && !scheduledTime;

  // --- Handlers ---

  const handleScheduledDateChange = (date) => {
    setScheduledDate(date || null); // gitleaks:allow
    setScheduledTime(null);
  };

  const filterTime = useCallback(
    (time) => {
      if (!scheduledDate) return true;

      const currentTime = new Date();
      const selectedDate = new Date(time);
      const fullDateTime = new Date(scheduledDate);

      fullDateTime.setHours(selectedDate.getHours());
      fullDateTime.setMinutes(selectedDate.getMinutes());
      fullDateTime.setSeconds(0);

      return (
        fullDateTime.getTime() > currentTime.getTime() &&
        fullDateTime.getTime() <= maxScheduleDate.getTime()
      );
    },
    [scheduledDate, maxScheduleDate]
  );

  const handleTimeChange = (time) => {
    if (!time || !scheduledDate) {
      setScheduledTime(null);

      return;
    }

    const merged = new Date(scheduledDate);

    merged.setHours(time.getHours());
    merged.setMinutes(time.getMinutes());
    merged.setSeconds(0);
    merged.setMilliseconds(0);

    // Reject past times and times beyond the scheduling window
    const currentTime = new Date();

    if (
      merged.getTime() <= currentTime.getTime() ||
      merged.getTime() > maxScheduleDate.getTime()
    ) {
      setScheduledTime(null);

      return;
    }

    setScheduledTime(merged);
  };

  const handleStartModeChange = (val) => {
    setStartMode(val === 1 ? START_MODE.NOW : START_MODE.SCHEDULE);
    setScheduledDate(null);
    setScheduledTime(null);
    setIsSubmitted(false);
  };

  const handleTogglePassphrase = () => {
    setShowPassphrase((prev) => !prev);
  };

  const resetAndClose = () => {
    setPassphrase('');
    setConfirmPassphrase('');
    onClose();
  };

  const handleSubmit = () => {
    setIsSubmitted(true);
    if (!canSubmit) return;

    setIsSubmitting(true);

    const payload = {
      slot: nextKeySlot,
      startTime: activeFrom.toISOString(),
      ...(passphrase && { passphrase }),
    };

    // Route through the app-level MFA challenge: on a 428 the shared MfaProvider
    // prompts for a TOTP code and retries with the code in the request body. A null
    // result means the user cancelled or hit a terminal MFA error (already
    // surfaced in the modal), so the download modal stays open for a retry.
    runWithMfa((mfaCode) => generateSshAccessKeyApi(payload, mfaCode), {
      onMfaRequired: () => setIsSubmitting(false),
    })
      .then((response) => {
        if (!response) return null;

        if (response.status === 200) {
          return response.blob().then((blob) => {
            downloadKeyZip(blob);
            onSubmit();
            setAlertBannerData((prev) => [
              ...prev,
              generateSuccessBanner(localize),
            ]);
            resetAndClose();
          });
        }

        setAlertBannerData((prev) => [
          ...prev,
          generateFailureBanner(localize),
        ]);
        resetAndClose();

        return null;
      })
      .catch(() => {
        setAlertBannerData((prev) => [
          ...prev,
          generateFailureBanner(localize),
        ]);
        resetAndClose();
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleCancel = () => {
    setPassphrase('');
    setConfirmPassphrase('');
    onClose();
  };

  return {
    // config passthrough for the presentational layer
    keyValidityHours,
    isPassphraseMandatory,
    minPassphraseLength,
    maxPassphraseLength,
    // state
    passphrase,
    confirmPassphrase,
    showPassphrase,
    startMode,
    scheduledDate,
    scheduledTime,
    isSubmitted,
    isSubmitting,
    // computed dates
    now,
    maxScheduleDate,
    activeFrom,
    expiresAt,
    // derived flags
    isScheduleMode,
    passphrasesMismatch,
    passphraseLengthInvalid,
    canSubmit,
    isPassphraseCritical,
    isConfirmCritical,
    isDateCritical,
    isTimeCritical,
    // handlers
    filterTime,
    handleScheduledDateChange,
    handleTimeChange,
    handleStartModeChange,
    handleTogglePassphrase,
    handleSetPassphrase: setPassphrase,
    handleSetConfirmPassphrase: setConfirmPassphrase,
    handleSubmit,
    handleCancel,
  };
};

export default useDownloadKeyForm;
